import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Chip,
  Avatar,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  Rating,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Tooltip,
  Badge
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WorkIcon from '@mui/icons-material/Work';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentsIcon from '@mui/icons-material/Payments';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SecurityIcon from '@mui/icons-material/Security';
import StarIcon from '@mui/icons-material/Star';
import DescriptionIcon from '@mui/icons-material/Description';
import BadgeIcon from '@mui/icons-material/Badge';
import LinkIcon from '@mui/icons-material/Link';
import SchoolIcon from '@mui/icons-material/School';
import GavelIcon from '@mui/icons-material/Gavel';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns';
import { supabase } from '../../../lib/supabase';
import { 
  Worker, 
  WorkerRole, 
  WorkerStatus, 
  PaymentType, 
  defaultPermissions 
} from '../../../types/worker';
import LeaveManagement from './LeaveManagement';
import PayrollManagement from './PayrollManagement';
import ShiftManagement from './ShiftManagement';
import RecruitmentManagement from './RecruitmentManagement';
import TrainingManagement from './TrainingManagement';
import DisciplinaryManagement from './DisciplinaryManagement';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const WorkerAvatar = styled(Avatar)(({ theme }) => ({
  width: 64,
  height: 64,
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  fontSize: '1.5rem',
}));

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.black, 0.05),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.black, 0.08),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
  ...(theme.palette.mode === 'dark' && {
    backgroundColor: alpha(theme.palette.common.white, 0.05),
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.white, 0.10),
    },
  }),
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
}));

// Constants
const workerRoles: WorkerRole[] = ['owner', 'manager', 'worker', 'viewer'];
const workerStatuses: WorkerStatus[] = ['active', 'on-leave', 'inactive'];
const paymentTypes: PaymentType[] = ['daily', 'weekly', 'monthly'];
const employmentTypes = ['full_time', 'part_time', 'seasonal', 'casual', 'contract'];
const specializations = [
  'Crop Management', 'Animal Care', 'Equipment Operation', 
  'Irrigation', 'Pest Control', 'Harvesting', 'Processing', 
  'Maintenance', 'Security', 'Logistics', 'Administration'
];

interface FarmWorkersProps {
  farm: any;
}

const FarmWorkers: React.FC<FarmWorkersProps> = ({ farm }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog States
  const [openDialog, setOpenDialog] = useState(false);
  const [currentWorker, setCurrentWorker] = useState<Partial<Worker>>({});
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workerToDelete, setWorkerToDelete] = useState<Worker | null>(null);
  const [activeTab, setActiveTab] = useState(0); // For Dialog tabs
  const [mainTab, setMainTab] = useState(0); // For Main Module tabs

  // Menu State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string | null>(null);

  // Platform Link State
  const [linkEmail, setLinkEmail] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkSuccess, setLinkSuccess] = useState<string | null>(null);

  const fetchWorkers = async () => {
    if (!farm?.id) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('workers')
        .select('*')
        .eq('farm_id', farm.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const mappedWorkers = (data || []).map((w: any) => ({
        ...w,
        phoneNumber: w.phone_number,
        paymentType: w.payment_type,
        workingHours: w.working_hours,
        leaveDays: w.leave_days,
        leaveHistory: w.leave_history,
        emergencyContact: w.emergency_contact,
        farmId: w.farm_id,
        department: w.department,
        jobTitle: w.job_title,
        employmentType: w.employment_type,
        bankDetails: w.bank_details,
        documents: w.documents,
        performanceRating: w.performance_rating,
        linkedUserId: w.linked_user_id
      }));

      setWorkers(mappedWorkers);
    } catch (err: any) {
      console.error('Error fetching workers:', err);
      setError('Failed to load workers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkers();
  }, [farm?.id]);

  // Summary Statistics
  const stats = useMemo(() => {
    const total = workers.length;
    const active = workers.filter(w => w.status === 'active').length;
    const onLeave = workers.filter(w => w.status === 'on-leave').length;
    const monthlyPayroll = workers.reduce((sum, w) => {
      let monthlyWage = 0;
      if (w.paymentType === 'monthly') monthlyWage = w.wage;
      else if (w.paymentType === 'weekly') monthlyWage = w.wage * 4;
      else if (w.paymentType === 'daily') monthlyWage = w.wage * 22;
      return sum + monthlyWage;
    }, 0);

    return { total, active, onLeave, monthlyPayroll };
  }, [workers]);

  // Filtered Workers
  const filteredWorkers = useMemo(() => {
    return workers.filter(worker => 
      worker.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      worker.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
      worker.status.toLowerCase().includes(searchQuery.toLowerCase()) ||
      worker.jobTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      worker.department?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [workers, searchQuery]);

  // Handlers
  const handleAddWorker = () => {
    setDialogMode('add');
    setCurrentWorker({
      name: '',
      role: 'worker',
      status: 'active',
      wage: 0,
      paymentType: 'monthly',
      startDate: format(new Date(), 'yyyy-MM-dd'),
      permissions: defaultPermissions['worker'],
      specialization: [],
      workingHours: { start: '08:00', end: '17:00' },
      emergencyContact: { name: '', relationship: '', phone: '' },
      employmentType: 'full_time',
      department: '',
      jobTitle: '',
      bankDetails: { bankName: '', accountNumber: '', accountName: '' },
      performanceRating: 0,
      notes: ''
    });
    setActiveTab(0);
    setOpenDialog(true);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, workerId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedWorkerId(workerId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedWorkerId(null);
  };

  const handleEditWorker = (worker: Worker) => {
    setDialogMode('edit');
    setCurrentWorker({ ...worker });
    setActiveTab(0);
    setOpenDialog(true);
    handleCloseMenu();
  };

  const handleDeleteClick = (worker: Worker) => {
    setWorkerToDelete(worker);
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };

  const handleDeleteConfirm = async () => {
    if (!workerToDelete) return;
    try {
      const { error } = await supabase
        .from('workers')
        .delete()
        .eq('id', workerToDelete.id);
      
      if (error) throw error;
      await fetchWorkers();
      setDeleteDialogOpen(false);
      setWorkerToDelete(null);
    } catch (err) {
      console.error('Error deleting worker:', err);
      setError('Failed to delete worker');
    }
  };

  const handleSaveWorker = async () => {
    try {
      setLoading(true);
      const workerData = {
        name: currentWorker.name,
        role: currentWorker.role,
        phone_number: currentWorker.phoneNumber,
        email: currentWorker.email,
        start_date: currentWorker.startDate,
        wage: currentWorker.wage,
        payment_type: currentWorker.paymentType,
        status: currentWorker.status,
        specialization: currentWorker.specialization,
        working_hours: currentWorker.workingHours,
        emergency_contact: currentWorker.emergencyContact,
        notes: currentWorker.notes,
        permissions: currentWorker.permissions,
        farm_id: farm.id,
        // New fields
        department: currentWorker.department,
        job_title: currentWorker.jobTitle,
        employment_type: currentWorker.employmentType,
        bank_details: currentWorker.bankDetails,
        performance_rating: currentWorker.performanceRating,
        documents: currentWorker.documents,
        linked_user_id: currentWorker.linkedUserId
      };

      if (dialogMode === 'add') {
        const { error } = await supabase
          .from('workers')
          .insert([workerData]);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('workers')
          .update(workerData)
          .eq('id', currentWorker.id);
        
        if (error) throw error;
      }
      await fetchWorkers();
      setOpenDialog(false);
    } catch (err) {
      console.error('Error saving worker:', err);
      setError('Failed to save worker');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkUser = async () => {
    if (!currentWorker.id || !linkEmail) return;
    setLinkLoading(true);
    setLinkError(null);
    setLinkSuccess(null);

    try {
      // Use RPC function for secure linking
      const { data: success, error } = await supabase
        .rpc('link_worker_to_user', {
          worker_uuid: currentWorker.id,
          user_email: linkEmail
        });

      if (error) throw error;
      if (!success) throw new Error('User not found with this email. Please ensure they have registered on the platform.');

      // Refresh to get the linked user ID
      await fetchWorkers();
      
      // Ideally we would get the ID back, but for now we reload
      // We can optimistically verify success
      setLinkSuccess('Successfully linked to platform user!');
      setLinkEmail('');
      
      // Update local state by re-fetching specific worker or just assuming success
      // Let's re-fetch to be safe
      const { data: updatedWorker } = await supabase
        .from('workers')
        .select('linked_user_id')
        .eq('id', currentWorker.id)
        .single();
        
      if (updatedWorker) {
        setCurrentWorker({ ...currentWorker, linkedUserId: updatedWorker.linked_user_id });
      }

    } catch (err: any) {
      setLinkError(err.message || 'Failed to link user');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleUnlinkUser = async () => {
    if (!currentWorker.id) return;
    try {
        const { error } = await supabase
            .from('workers')
            .update({ linked_user_id: null })
            .eq('id', currentWorker.id);
        
        if (error) throw error;
        setCurrentWorker({ ...currentWorker, linkedUserId: undefined });
        await fetchWorkers();
    } catch (err) {
        console.error('Error unlinking user:', err);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Paper sx={{ mb: 3 }}>
        <Tabs 
          value={mainTab} 
          onChange={(_, v) => setMainTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<GroupIcon />} label="Workforce" iconPosition="start" />
          <Tab icon={<AccessTimeIcon />} label="Shifts" iconPosition="start" />
          <Tab icon={<EventBusyIcon />} label="Leave" iconPosition="start" />
          <Tab icon={<PaymentsIcon />} label="Payroll" iconPosition="start" />
          <Tab icon={<SchoolIcon />} label="Training" iconPosition="start" />
          <Tab icon={<GavelIcon />} label="Disciplinary" iconPosition="start" />
          <Tab icon={<PersonAddIcon />} label="Recruitment" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Workforce Tab */}
      <Box role="tabpanel" hidden={mainTab !== 0}>
        {mainTab === 0 && (
          <>
            {/* Header & Stats */}
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
                    <CardContent>
                      <Typography variant="overline" sx={{ opacity: 0.8 }}>Total Workers</Typography>
                      <Typography variant="h3" fontWeight="bold">{stats.total}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <GroupIcon sx={{ mr: 1, opacity: 0.8 }} />
                        <Typography variant="body2">Active Workforce</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="overline" color="text.secondary">Active</Typography>
                      <Typography variant="h3" color="success.main" fontWeight="bold">{stats.active}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">Currently working</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="overline" color="text.secondary">On Leave</Typography>
                      <Typography variant="h3" color="warning.main" fontWeight="bold">{stats.onLeave}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <EventBusyIcon color="warning" sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">Away today</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="overline" color="text.secondary">Monthly Payroll</Typography>
                      <Typography variant="h3" color="info.main" fontWeight="bold">
                        ${stats.monthlyPayroll.toLocaleString()}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <PaymentsIcon color="info" sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">Estimated cost</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Actions Bar */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
                <Search>
                  <SearchIconWrapper>
                    <SearchIcon />
                  </SearchIconWrapper>
                  <TextField
                    placeholder="Search workers..."
                    variant="standard"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      disableUnderline: true,
                      sx: { ml: 6, width: '100%', color: 'inherit' }
                    }}
                  />
                </Search>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(_, value) => value && setViewMode(value)}
                  size="small"
                >
                  <ToggleButton value="grid"><ViewModuleIcon /></ToggleButton>
                  <ToggleButton value="list"><ViewListIcon /></ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddWorker}
              >
                Add Worker
              </Button>
            </Box>

            {/* Workers List */}
            {viewMode === 'list' ? (
              <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Job Title</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredWorkers.map((worker) => (
                      <TableRow key={worker.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar>{worker.name.charAt(0)}</Avatar>
                            <Box>
                              <Typography variant="subtitle2">{worker.name}</Typography>
                              {worker.linkedUserId && (
                                <Tooltip title="Linked to Platform User">
                                  <BadgeIcon fontSize="small" color="primary" sx={{ width: 14, height: 14 }} />
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{worker.jobTitle || '-'}</TableCell>
                        <TableCell>{worker.department || '-'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={worker.role} 
                            size="small" 
                            color={worker.role === 'manager' ? 'primary' : 'default'}
                            variant="outlined"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{worker.phoneNumber}</Typography>
                          <Typography variant="caption" color="text.secondary">{worker.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={worker.status} 
                            size="small" 
                            color={worker.status === 'active' ? 'success' : worker.status === 'inactive' ? 'error' : 'warning'}
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Rating value={worker.performanceRating || 0} readOnly size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={(e) => handleMenuClick(e, worker.id)}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Grid container spacing={3}>
                {filteredWorkers.map((worker) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={worker.id}>
                    <StyledCard>
                      <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                          <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            badgeContent={
                              <Box sx={{ 
                                width: 12, 
                                height: 12, 
                                bgcolor: worker.status === 'active' ? 'success.main' : 'warning.main',
                                borderRadius: '50%',
                                border: '2px solid white'
                              }} />
                            }
                          >
                            <WorkerAvatar>{worker.name.charAt(0)}</WorkerAvatar>
                          </Badge>
                        </Box>
                        <Typography variant="h6" gutterBottom>{worker.name}</Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {worker.jobTitle || worker.role}
                        </Typography>
                        {worker.department && (
                          <Chip label={worker.department} size="small" sx={{ mb: 2 }} />
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                          {worker.linkedUserId && (
                            <Tooltip title="Linked Account">
                              <BadgeIcon color="primary" fontSize="small" />
                            </Tooltip>
                          )}
                          <Tooltip title="Performance">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <StarIcon color="warning" fontSize="small" />
                              <Typography variant="caption" sx={{ ml: 0.5 }}>
                                {worker.performanceRating || 0}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Box>
                      </CardContent>
                      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                        <Button size="small" startIcon={<PhoneIcon />} href={`tel:${worker.phoneNumber}`}>
                          Call
                        </Button>
                        <Button size="small" onClick={() => handleEditWorker(worker)}>
                          View Profile
                        </Button>
                      </Box>
                    </StyledCard>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Box>

      {/* Shifts Tab */}
      <Box role="tabpanel" hidden={mainTab !== 1}>
        {mainTab === 1 && <ShiftManagement farmId={farm.id} workers={workers} />}
      </Box>

      {/* Leave Tab */}
      <Box role="tabpanel" hidden={mainTab !== 2}>
        {mainTab === 2 && <LeaveManagement farmId={farm.id} workers={workers} />}
      </Box>

      {/* Payroll Tab */}
      <Box role="tabpanel" hidden={mainTab !== 3}>
        {mainTab === 3 && <PayrollManagement farmId={farm.id} workers={workers} />}
      </Box>

      {/* Training Tab */}
      <Box role="tabpanel" hidden={mainTab !== 4}>
        {mainTab === 4 && <TrainingManagement farmId={farm.id} workers={workers} />}
      </Box>

      {/* Disciplinary Tab */}
      <Box role="tabpanel" hidden={mainTab !== 5}>
        {mainTab === 5 && <DisciplinaryManagement farmId={farm.id} workers={workers} />}
      </Box>

      {/* Recruitment Tab */}
      <Box role="tabpanel" hidden={mainTab !== 6}>
        {mainTab === 6 && <RecruitmentManagement farmId={farm.id} />}
      </Box>

      {/* Worker Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => {
          const w = workers.find(w => w.id === selectedWorkerId);
          if (w) handleEditWorker(w);
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          const w = workers.find(w => w.id === selectedWorkerId);
          if (w) handleDeleteClick(w);
        }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ color: 'error' }}>Delete Worker</ListItemText>
        </MenuItem>
      </Menu>

      {/* Comprehensive Worker Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WorkerAvatar sx={{ width: 40, height: 40, mb: 0 }}>
              {currentWorker.name?.charAt(0) || <AddIcon />}
            </WorkerAvatar>
            <Box>
              <Typography variant="h6">
                {dialogMode === 'add' ? 'Add New Worker' : 'Edit Worker Profile'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Employee ID: {currentWorker.id?.slice(0, 8) || 'New'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ width: '100%', typography: 'body1' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
                <Tab label="Personal & Contact" icon={<PhoneIcon />} iconPosition="start" />
                <Tab label="Employment" icon={<WorkIcon />} iconPosition="start" />
                <Tab label="Compensation" icon={<PaymentsIcon />} iconPosition="start" />
                <Tab label="Access & Role" icon={<SecurityIcon />} iconPosition="start" />
                <Tab label="Performance" icon={<StarIcon />} iconPosition="start" />
              </Tabs>
            </Box>

            {/* Tab 0: Personal */}
            <Box hidden={activeTab !== 0} sx={{ py: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={currentWorker.name || ''}
                    onChange={(e) => setCurrentWorker({...currentWorker, name: e.target.value})}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={currentWorker.phoneNumber || ''}
                    onChange={(e) => setCurrentWorker({...currentWorker, phoneNumber: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={currentWorker.email || ''}
                    onChange={(e) => setCurrentWorker({...currentWorker, email: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Emergency Contact</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Contact Name"
                        value={currentWorker.emergencyContact?.name || ''}
                        onChange={(e) => setCurrentWorker({
                          ...currentWorker, 
                          emergencyContact: { ...currentWorker.emergencyContact!, name: e.target.value }
                        })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Relationship"
                        value={currentWorker.emergencyContact?.relationship || ''}
                        onChange={(e) => setCurrentWorker({
                          ...currentWorker, 
                          emergencyContact: { ...currentWorker.emergencyContact!, relationship: e.target.value }
                        })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Emergency Phone"
                        value={currentWorker.emergencyContact?.phone || ''}
                        onChange={(e) => setCurrentWorker({
                          ...currentWorker, 
                          emergencyContact: { ...currentWorker.emergencyContact!, phone: e.target.value }
                        })}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>

            {/* Tab 1: Employment */}
            <Box hidden={activeTab !== 1} sx={{ py: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Job Title"
                    placeholder="e.g. Senior Farm Hand"
                    value={currentWorker.jobTitle || ''}
                    onChange={(e) => setCurrentWorker({...currentWorker, jobTitle: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Department</InputLabel>
                    <Select
                      value={currentWorker.department || ''}
                      label="Department"
                      onChange={(e) => setCurrentWorker({...currentWorker, department: e.target.value})}
                    >
                      <MenuItem value="Operations">Operations</MenuItem>
                      <MenuItem value="Crops">Crops</MenuItem>
                      <MenuItem value="Livestock">Livestock</MenuItem>
                      <MenuItem value="Maintenance">Maintenance</MenuItem>
                      <MenuItem value="Logistics">Logistics</MenuItem>
                      <MenuItem value="Admin">Admin</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Employment Type</InputLabel>
                    <Select
                      value={currentWorker.employmentType || 'full_time'}
                      label="Employment Type"
                      onChange={(e) => setCurrentWorker({...currentWorker, employmentType: e.target.value as any})}
                    >
                      {employmentTypes.map(type => (
                        <MenuItem key={type} value={type} sx={{ textTransform: 'capitalize' }}>
                          {type.replace('_', ' ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={currentWorker.status || 'active'}
                      label="Status"
                      onChange={(e) => setCurrentWorker({...currentWorker, status: e.target.value as WorkerStatus})}
                    >
                      {workerStatuses.map(status => (
                        <MenuItem key={status} value={status} sx={{ textTransform: 'capitalize' }}>{status}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start Date"
                    value={currentWorker.startDate || ''}
                    onChange={(e) => setCurrentWorker({...currentWorker, startDate: e.target.value})}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Specialization</InputLabel>
                    <Select
                      multiple
                      value={currentWorker.specialization || []}
                      label="Specialization"
                      onChange={(e) => setCurrentWorker({...currentWorker, specialization: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value})}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {specializations.map((spec) => (
                        <MenuItem key={spec} value={spec}>{spec}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {/* Tab 2: Compensation */}
            <Box hidden={activeTab !== 2} sx={{ py: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Wage / Salary Amount"
                    value={currentWorker.wage || 0}
                    onChange={(e) => setCurrentWorker({...currentWorker, wage: Number(e.target.value)})}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Frequency</InputLabel>
                    <Select
                      value={currentWorker.paymentType || 'monthly'}
                      label="Payment Frequency"
                      onChange={(e) => setCurrentWorker({...currentWorker, paymentType: e.target.value as PaymentType})}
                    >
                      {paymentTypes.map(type => (
                        <MenuItem key={type} value={type} sx={{ textTransform: 'capitalize' }}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider textAlign="left"><Chip label="Bank Account Details" size="small" /></Divider>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Bank Name"
                    value={currentWorker.bankDetails?.bankName || ''}
                    onChange={(e) => setCurrentWorker({
                      ...currentWorker,
                      bankDetails: { ...currentWorker.bankDetails!, bankName: e.target.value }
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account Name"
                    value={currentWorker.bankDetails?.accountName || ''}
                    onChange={(e) => setCurrentWorker({
                      ...currentWorker,
                      bankDetails: { ...currentWorker.bankDetails!, accountName: e.target.value }
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Account Number"
                    value={currentWorker.bankDetails?.accountNumber || ''}
                    onChange={(e) => setCurrentWorker({
                      ...currentWorker,
                      bankDetails: { ...currentWorker.bankDetails!, accountNumber: e.target.value }
                    })}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Tab 3: Access & Role */}
            <Box hidden={activeTab !== 3} sx={{ py: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Assigning a role determines what the worker can do on the platform. Linking a user account allows them to log in.
                  </Alert>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Platform Role</InputLabel>
                    <Select
                      value={currentWorker.role || 'worker'}
                      label="Platform Role"
                      onChange={(e) => setCurrentWorker({...currentWorker, role: e.target.value as WorkerRole})}
                    >
                      {workerRoles.map(role => (
                        <MenuItem key={role} value={role} sx={{ textTransform: 'capitalize' }}>{role}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <SecurityIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight="bold">Platform Access</Typography>
                    </Box>
                    
                    {currentWorker.linkedUserId ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="body2" color="success.main" fontWeight="bold">
                            <CheckCircleIcon fontSize="small" sx={{ verticalAlign: 'text-bottom', mr: 0.5 }} />
                            Account Linked
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            This worker profile is linked to a registered user account.
                          </Typography>
                        </Box>
                        <Button 
                          variant="outlined" 
                          color="error" 
                          size="small"
                          onClick={handleUnlinkUser}
                        >
                          Unlink Account
                        </Button>
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="body2" paragraph>
                          Enter the email address of the registered user to link this worker profile to their account.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="user@email.com"
                            value={linkEmail}
                            onChange={(e) => setLinkEmail(e.target.value)}
                          />
                          <Button 
                            variant="contained" 
                            disabled={!linkEmail || linkLoading}
                            onClick={handleLinkUser}
                          >
                            {linkLoading ? 'Linking...' : 'Link'}
                          </Button>
                        </Box>
                        {linkError && <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>{linkError}</Typography>}
                        {linkSuccess && <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>{linkSuccess}</Typography>}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            {/* Tab 4: Performance & Notes */}
            <Box hidden={activeTab !== 4} sx={{ py: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography component="legend">Performance Rating</Typography>
                  <Rating
                    name="performance-rating"
                    value={currentWorker.performanceRating || 0}
                    onChange={(_, newValue) => {
                      setCurrentWorker({...currentWorker, performanceRating: newValue || 0});
                    }}
                    size="large"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Notes / Performance Review"
                    placeholder="Add notes about performance, skills, or incidents..."
                    value={currentWorker.notes || ''}
                    onChange={(e) => setCurrentWorker({...currentWorker, notes: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Documents</Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 4, 
                      textAlign: 'center', 
                      borderStyle: 'dashed', 
                      bgcolor: alpha(theme.palette.background.default, 0.5),
                      cursor: 'pointer'
                    }}
                  >
                    <DescriptionIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography color="text.secondary">
                      Drag and drop files here or click to upload
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      (Contracts, ID Copies, Certifications)
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveWorker} variant="contained" color="primary">
            Save Worker
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {workerToDelete?.name}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FarmWorkers;
